import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Signal, computed, inject, input } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { capitalize as _capitalize, memoize as _memoize } from 'lodash/fp'
import { TooltipModule } from 'primeng/tooltip'

import { FlxAccordionModule, FlxModalService } from '@flowx/angular-ui-toolkit'
import { BpmnEditorIconsModule } from '@flowx/designer/modules/bpmn-editor/bpmn-editor-icons.module'
import { SliceType } from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import {
  DataModelReference,
  DataModelReferenceLocation,
  DataModelReferenceType,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { FlxClickStopPropagationDirective } from '@flowx/designer/modules/core/directives/stop-propagation.directive'
import { NODE_FOCUS_QUERY_PARAM } from '@flowx/designer/modules/integration-designer/models/workflow.entity'
import { ActionSection } from '@flowx/designer/modules/node-actions/components/action-edit/action-edit.dictionary'
import { NavigatorIconsModule } from '@flowx/designer/modules/ui-designer/components/config/new-template-config-tree/navigator-icons.module'
import { DataModelResourceType } from '@flowx/designer/shared/api/data-model.api'
import { ApplicationResourceType } from '@flowx/designer/shared/entities/application.entity'
import { ActivityNode } from '@flowx/designer/shared/entities/node.types'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'
import { selectCurrentResourceId } from '@flowx/designer/shared/store/selectors/data-model.selectors'

export const NodeIcons = {
  [ActivityNode.USER_TASK]: 'USER',
  [ActivityNode.TASK]: 'COG',
}

export const ReferenceTypeLabel = {
  [DataModelReferenceType.UI_ELEMENT]: 'UI Template',
  [DataModelReferenceType.DATA_MODEL_SLICE]: 'Parameter',
  [DataModelReferenceType.DATA_MAPPER_SLICE]: ' Mapping Variable',
  [DataModelReferenceType.TEMPLATE_FILE_SOURCE]: 'UI Template',
  [DataModelReferenceType.UI_ACTION_FILE_SOURCE]: 'UI Template',
}
export const ReferenceLocationLabel: Record<DataModelReferenceLocation, string | null> = {
  [DataModelReferenceLocation.PROCESS_KEY]: 'Key',
  [DataModelReferenceLocation.REUSABLE_UI_TEMPLATE_KEY]: 'Key',
  [DataModelReferenceLocation.UI_FLOW_KEY]: 'Key',
  [DataModelReferenceLocation.GENERIC]: null,
  [DataModelReferenceLocation.DOCUMENT]: null,
}

export const ResourceTypeLabel = {
  [ApplicationResourceType.ReusableTemplate]: 'UI Template',
  [ApplicationResourceType.ReusableFunction]: 'Function',
  [ApplicationResourceType.Process]: 'Process',
  [ApplicationResourceType.Workflow]: 'Workflow',
  [ApplicationResourceType.UiFlow]: 'UI Flow',
}

export const referenceNodeName = (reference: DataModelReference): string =>
  reference?.nodeDefinitionName ?? reference?.nodeName

type UIReference = Omit<DataModelReference, 'type'> & {
  type: string
  context: {
    label: string
    icon: string
  }[]
  routerLink: string[]
  routerLinkParams: Record<string, any>
}

@Component({
  selector: 'flx-attribute-references',
  imports: [
    CommonModule,
    FlxAccordionModule,
    NavigatorIconsModule,
    BpmnEditorIconsModule,
    TooltipModule,
    FlxClickStopPropagationDirective,
  ],
  templateUrl: './attribute-references.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './attribute-references.component.scss',
})
export class AttributeReferencesComponent {
  #store = inject(Store)
  #urlParamStore = inject(UrlParametersStoreService)
  #router = inject(Router)
  #flxModalService = inject(FlxModalService)

  path = input.required<string>()
  references = input.required<DataModelReference[]>()
  isCollapsable = input<boolean>(false)
  isCollapsed = input<boolean>(true)
  resourceType = input.required<DataModelResourceType>()

  columns = [
    { field: 'type', header: 'Reference type', width: '30%' },
    { field: 'context', header: 'Context', width: '60%' },
    { field: 'goTo', header: '', width: '10%' },
  ]

  header = computed(() => `${this.path()} (${this.uiReferences().length})`)
  resourceId = toSignal(this.#store.select(selectCurrentResourceId))

  uiReferences: Signal<UIReference[]> = computed(() => {
    const references = this.references()
    const resourceId = this.resourceId()
    const resourceType = this.resourceType()

    return references
      .map((reference) => this.#computeReferenceEntry(reference, resourceId, resourceType))
      .map((entry) => ({
        ...entry,
        // A segment the host does not have would otherwise render as a bare "\\" separator.
        context: entry.context.filter(({ label }) => !!label?.trim()),
      }))
  })

  getContextLabels = _memoize((context: { label: string }[]): string =>
    context.map((c) => _capitalize(c.label)).join(' \\ ')
  )

  goToReference(reference: UIReference): void {
    this.#router
      .navigate(reference.routerLink, {
        queryParams: reference.routerLinkParams,
      })
      .then(() => this.#flxModalService.hide())
  }

  #computeReferenceEntry(
    reference: DataModelReference,
    resourceId: string,
    resourceType: DataModelResourceType
  ): UIReference {
    switch (reference.type) {
      case DataModelReferenceType.UI_ELEMENT:
      case DataModelReferenceType.TEMPLATE_FILE_SOURCE:
      case DataModelReferenceType.UI_ACTION_FILE_SOURCE:
        return this.#computeUiElementReferenceEntry(reference, resourceId, resourceType)
      case DataModelReferenceType.DATA_MODEL_SLICE:
        return this.#computeSliceReferenceEntry(reference, resourceId, resourceType)
      case DataModelReferenceType.DATA_MAPPER_SLICE:
        return this.#computeDataMapperReferenceEntry(reference, resourceId, resourceType)
    }
  }

  #computeUiElementReferenceEntry(
    reference: DataModelReference,
    resourceId: string,
    resourceType: DataModelResourceType
  ): UIReference {
    const hasNavigationArea = resourceType === ApplicationResourceType.Process
    const locationLabel = ReferenceLocationLabel[reference?.location]
    const uiLinkPathFromResource = this.#getUILinkPathFromResource(resourceType, resourceId)
    return {
      ...reference,
      type: ReferenceTypeLabel[reference.type],
      context: [
        ...(reference?.nodeDefinitionName
          ? [
              {
                label: reference.nodeDefinitionName,
                icon: NodeIcons[reference?.nodeType],
              },
            ]
          : []),
        {
          label: reference?.entityType.toLowerCase().replace(/_/g, ' '),
          icon: reference?.entityType,
        },
        ...(reference?.actionName ? [{ icon: null, label: reference.actionName }] : []),
        ...(locationLabel ? [{ icon: null, label: locationLabel }] : []),
      ],
      routerLink: [
        ...uiLinkPathFromResource,
        ...(hasNavigationArea
          ? [`${reference?.navigationInfo?.uiTemplateInfo?.navigationAreaId}`]
          : []),
        'ui',
      ],
      routerLinkParams: {
        templateIds: reference?.navigationInfo?.uiTemplateInfo?.uiTemplateId,
        ...(hasNavigationArea
          ? {
              pivotAreaId: reference?.navigationInfo?.uiTemplateInfo?.navigationAreaId,
              swimlaneId: reference?.navigationInfo?.uiTemplateInfo?.swimlaneId,
            }
          : {}),
      },
    }
  }

  #computeSliceReferenceEntry(
    reference: DataModelReference,
    resourceId: string,
    resourceType: DataModelResourceType
  ): UIReference {
    return {
      ...reference,
      type: `${reference.nodeDefinitionName ? ResourceTypeLabel[resourceType] : ''} ${ReferenceTypeLabel[reference.type]}`,
      context: [
        {
          label: `${_capitalize(reference?.entityType)} Parameter`,
          icon: '',
        },
        ...(reference.nodeDefinitionName
          ? [
              {
                label: ` ${reference.nodeDefinitionName}`,
                icon: '',
              },
            ]
          : []),
      ],
      routerLink: [...this.#getUILinkPathFromResource(resourceType, resourceId), 'data-model'],
      routerLinkParams: {
        dataModelTab: `${reference?.entityType.toLowerCase()}-parameters`,
        ...(resourceType === ApplicationResourceType.Process && reference?.nodeDefinitionId
          ? reference?.entityType === 'INPUT'
            ? { inputParametersNodeId: reference.nodeDefinitionId }
            : reference?.entityType === 'OUTPUT'
              ? { outputParametersNodeId: reference.nodeDefinitionId }
              : {}
          : {}),
        ...(resourceType === ApplicationResourceType.Workflow && reference?.nodeFlowxUuid
          ? { outputParametersNodeId: reference.nodeFlowxUuid }
          : {}),
      },
    }
  }

  #computeDataMapperReferenceEntry(
    reference: DataModelReference,
    resourceId: string,
    resourceType: DataModelResourceType
  ): UIReference {
    const isWorkflow = resourceType === ApplicationResourceType.Workflow
    const resourcePath = this.#getUILinkPathFromResource(resourceType, resourceId)

    return {
      ...reference,
      nodeDefinitionName: referenceNodeName(reference),
      type: `${_capitalize(reference?.entityType.toLowerCase())} ${ReferenceTypeLabel[reference.type]}`,
      context: [
        {
          label: referenceNodeName(reference),
          icon: NodeIcons[reference?.nodeType],
        },
        {
          label: reference?.actionName,
          icon: '',
        },
        {
          label: `${reference?.entityType.toLowerCase()} Mapping`,
          icon: SliceType.INPUT,
        },
      ],
      routerLink: isWorkflow ? [...resourcePath, 'editor'] : [...resourcePath, 'diagram'],
      routerLinkParams: isWorkflow
        ? { [NODE_FOCUS_QUERY_PARAM]: reference?.nodeFlowxUuid }
        : {
            nodeId: reference?.navigationInfo?.actionInfo?.nodeDefinitionId,
            actionId: reference?.navigationInfo?.actionInfo?.actionDefinitionId,
            actionSection: ActionSection.dataMapping,
            nodePanelOpened: true,
          },
    }
  }

  #getUILinkPathFromResource(resourceType: DataModelResourceType, resourceId: string): string[] {
    const { appId, appVersionId, workspaceId } = this.#urlParamStore.urlParameters
    let resourceLink = []
    switch (resourceType) {
      case ApplicationResourceType.ReusableFunction:
        resourceLink = ['reusable-resources', 'functions']
        break
      case ApplicationResourceType.ReusableTemplate:
        resourceLink = ['reusable-resources', 'ui-templates']
        break
      case ApplicationResourceType.Process:
        resourceLink = ['processes']
        break
      case ApplicationResourceType.Workflow:
        resourceLink = ['integration-designer', 'workflows']
        break
      case ApplicationResourceType.UiFlow:
        resourceLink = ['ui-flows']
        break
    }
    return [
      'guard',
      'workspace',
      workspaceId,
      'projects',
      appId,
      'config',
      appVersionId,
      ...resourceLink,
      resourceId,
    ]
  }
}
